import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  UserCog, 
  Shield, 
  Edit, 
  Trash2, 
  Search,
  Check,
  X,
  User as UserIcon,
  Building2,
  Ticket,
  Home,
  Users,
  Settings,
  Eye,
  ChevronDown,
  ChevronUp,
  Plus
} from 'lucide-react';

const FEATURE_ICONS = {
  intranet: Home,
  tischplan: Users,
  tickets: Ticket,
  nutzerverwaltung: Settings
};

const PERMISSION_LEVELS = [
  { value: 'none', label: 'Keine', color: 'bg-gray-200 text-gray-600' },
  { value: 'view', label: 'Lesen', color: 'bg-blue-100 text-blue-700' },
  { value: 'edit', label: 'Bearbeiten', color: 'bg-green-100 text-green-700' },
  { value: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-700' }
];

const ROLES = [
  { value: 'basic', label: 'Basis', icon: UserIcon },
  { value: 'admin', label: 'Admin', icon: Shield }
];

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState({});

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'basic',
    department_id: '',
    permissions: [],
    is_active: true
  });

  // Create form state
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'basic',
    department_id: ''
  });

  // Hostesses
  const [tab, setTab] = useState('users');
  const [hostesses, setHostesses] = useState([]);
  const [showHostessDialog, setShowHostessDialog] = useState(false);
  const [editingHostess, setEditingHostess] = useState(null);
  const [hostessForm, setHostessForm] = useState({ name: '', pin: '', is_active: true });
  const [revealedPins, setRevealedPins] = useState({}); // { [id]: true }

  const fetchHostesses = async () => {
    try {
      const r = await axios.get('/admin/hostesses');
      setHostesses(r.data || []);
    } catch (err) { /* silent */ }
  };

  useEffect(() => { fetchHostesses(); }, []);

  const openCreateHostess = () => {
    setEditingHostess(null);
    setHostessForm({ name: '', pin: '', is_active: true });
    setShowHostessDialog(true);
  };

  const openEditHostess = (h) => {
    setEditingHostess(h);
    setHostessForm({ name: h.name || '', pin: h.hostess_pin || '', is_active: h.is_active !== false });
    setShowHostessDialog(true);
  };

  const saveHostess = async () => {
    if (!hostessForm.name.trim()) { toast.error('Name erforderlich'); return; }
    if (!hostessForm.pin.trim() || hostessForm.pin.length < 4) { toast.error('PIN muss mindestens 4 Zeichen haben'); return; }
    if (!/^[A-Za-z0-9]+$/.test(hostessForm.pin)) { toast.error('PIN darf nur Buchstaben und Ziffern enthalten'); return; }
    try {
      if (editingHostess) {
        await axios.patch(`/admin/hostesses/${editingHostess.id}`, hostessForm);
        toast.success('Hostess aktualisiert');
      } else {
        await axios.post('/admin/hostesses', hostessForm);
        toast.success('Hostess angelegt');
      }
      setShowHostessDialog(false);
      fetchHostesses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  const deleteHostess = async (h) => {
    if (!window.confirm(`Hostess "${h.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`/admin/hostesses/${h.id}`);
      toast.success('Gelöscht');
      fetchHostesses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const togglePinReveal = (id) => setRevealedPins(prev => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, featuresRes] = await Promise.all([
        axios.get('/admin/users'),
        axios.get('/admin/features')
      ]);
      setUsers(usersRes.data.users || []);
      setDepartments(usersRes.data.departments || []);
      setFeatures(featuresRes.data.features || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'basic',
      department_id: user.department_id || '',
      permissions: user.permissions || [],
      is_active: user.is_active !== false
    });
    setShowEditDialog(true);
  };

  const saveUser = async () => {
    if (!editingUser) return;
    
    try {
      const payload = {
        name: editForm.name,
        role: editForm.role,
        department_id: editForm.department_id || null,
        permissions: editForm.permissions,
        is_active: editForm.is_active
      };
      // Only send email if changed
      if (editForm.email && editForm.email !== editingUser.email) {
        payload.email = editForm.email;
      }
      // Only send password if filled
      if (editForm.password) {
        payload.password = editForm.password;
      }
      await axios.patch(`/admin/users/${editingUser.id}`, payload);
      toast.success('Benutzer aktualisiert');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const createUser = async () => {
    if (!createForm.email || !createForm.password) {
      toast.error('E-Mail und Passwort sind erforderlich');
      return;
    }
    if (createForm.password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    try {
      await axios.post('/auth/register', {
        email: createForm.email,
        password: createForm.password,
        name: createForm.name,
        role: createForm.role,
        department_id: createForm.department_id || null
      });
      toast.success('Benutzer erfolgreich erstellt');
      setShowCreateDialog(false);
      setCreateForm({ email: '', password: '', name: '', role: 'basic', department_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('Benutzer wirklich löschen?')) return;
    
    try {
      await axios.delete(`/admin/users/${userId}`);
      toast.success('Benutzer gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Update individual feature permission in edit form
  const updateFeaturePermission = (featureId, level, subPerms = []) => {
    const existing = editForm.permissions.filter(p => p.feature_id !== featureId);
    
    if (level !== 'none') {
      existing.push({
        feature_id: featureId,
        level: level,
        sub_permissions: subPerms
      });
    }
    
    setEditForm({ ...editForm, permissions: existing });
  };

  // Get current permission level from edit form
  const getFormPermissionLevel = (featureId) => {
    const perm = editForm.permissions.find(p => p.feature_id === featureId);
    return perm?.level || 'none';
  };

  // Get sub-permissions from edit form
  const getFormSubPermissions = (featureId) => {
    const perm = editForm.permissions.find(p => p.feature_id === featureId);
    return perm?.sub_permissions || [];
  };

  // Toggle sub-permission
  const toggleSubPermission = (featureId, subPerm) => {
    const currentLevel = getFormPermissionLevel(featureId);
    const currentSubPerms = getFormSubPermissions(featureId);
    
    let newSubPerms;
    if (currentSubPerms.includes(subPerm)) {
      newSubPerms = currentSubPerms.filter(s => s !== subPerm);
    } else {
      newSubPerms = [...currentSubPerms, subPerm];
    }
    
    updateFeaturePermission(featureId, currentLevel || 'view', newSubPerms);
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-500';
      case 'hostess': return 'bg-pink-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#e30613] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <UserCog className="w-6 h-6 text-[#e30613]" />
          Nutzerverwaltung
        </h1>
        {tab === 'users' && (
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-user-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Neuer Benutzer
          </Button>
        )}
        {tab === 'hostesses' && (
          <Button onClick={openCreateHostess} data-testid="create-hostess-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Neue Hostess
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">Benutzer ({users.length})</TabsTrigger>
          <TabsTrigger value="hostesses" data-testid="tab-hostesses">Hostessen ({hostesses.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4 mt-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Benutzer suchen..."
              className="pl-10"
            />
          </div>

      {/* Users List */}
      <div className="space-y-3">
        {filteredUsers.map((user) => {
          const isExpanded = expandedUsers[user.id];
          const dept = departments.find(d => d.id === user.department_id);
          const RoleIcon = ROLES.find(r => r.value === user.role)?.icon || UserIcon;
          
          return (
            <Card key={user.id} className={!user.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                {/* Main Row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${getRoleBadgeColor(user.role)}`}>
                      <RoleIcon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{user.name || user.email}</span>
                        {!user.is_active && <Badge variant="secondary">Inaktiv</Badge>}
                      </div>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {ROLES.find(r => r.value === user.role)?.label || user.role}
                        </Badge>
                        {dept && (
                          <Badge variant="outline" style={{ borderColor: dept.color, color: dept.color }}>
                            <Building2 className="w-3 h-3 mr-1" />
                            {dept.name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleUserExpand(user.id)}
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEditDialog(user)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    {user.id !== currentUser?.id && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => deleteUser(user.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-semibold mb-2">Individuelle Feature-Berechtigungen:</h4>
                    {(user.permissions || []).length === 0 ? (
                      <p className="text-sm text-gray-500">
                        Keine individuellen Berechtigungen. Erbt von Abteilung/Rolle.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {(user.permissions || []).map((perm) => {
                          const feature = features.find(f => f.id === perm.feature_id);
                          const levelInfo = PERMISSION_LEVELS.find(l => l.value === perm.level);
                          const FeatureIcon = FEATURE_ICONS[perm.feature_id] || Settings;
                          return (
                            <Badge 
                              key={perm.feature_id} 
                              className={levelInfo?.color || 'bg-gray-100'}
                            >
                              <FeatureIcon className="w-3 h-3 mr-1" />
                              {feature?.name || perm.feature_id}: {levelInfo?.label || perm.level}
                              {perm.sub_permissions?.length > 0 && (
                                <span className="ml-1 text-xs">
                                  (+{perm.sub_permissions.length})
                                </span>
                              )}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
        </TabsContent>

        <TabsContent value="hostesses" className="space-y-4 mt-4">
          {hostesses.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-gray-500">Keine Hostessen angelegt. Klicke &quot;Neue Hostess&quot; oben rechts.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {hostesses.map(h => {
                const revealed = revealedPins[h.id];
                return (
                  <Card key={h.id} className={!h.is_active ? 'opacity-60' : ''} data-testid={`hostess-row-${h.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center text-white font-bold">
                        {(h.name || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{h.name}</span>
                          {!h.is_active && <Badge variant="secondary">Inaktiv</Badge>}
                          <Badge className="bg-pink-500 text-white">Hostess</Badge>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                          <span>PIN:</span>
                          <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono text-xs" data-testid={`hostess-pin-${h.id}`}>
                            {revealed ? h.hostess_pin : '••••••'}
                          </code>
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => togglePinReveal(h.id)} data-testid={`toggle-pin-${h.id}`} title={revealed ? 'PIN verbergen' : 'PIN anzeigen'}>
                            {revealed ? <ChevronUp className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </Button>
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => openEditHostess(h)} data-testid={`edit-hostess-${h.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => deleteHostess(h)} data-testid={`delete-hostess-${h.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Hostess Dialog */}
      <Dialog open={showHostessDialog} onOpenChange={setShowHostessDialog}>
        <DialogContent data-testid="hostess-dialog">
          <DialogHeader>
            <DialogTitle>{editingHostess ? 'Hostess bearbeiten' : 'Neue Hostess anlegen'}</DialogTitle>
            <DialogDescription>Jede Hostess bekommt einen eigenen PIN für den Tischplan-Zugang.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={hostessForm.name} onChange={e => setHostessForm(p => ({ ...p, name: e.target.value }))} placeholder="z.B. Anna Schmidt" data-testid="hostess-name-input" />
            </div>
            <div>
              <Label>PIN * <span className="text-xs text-gray-400 font-normal">(min. 4 Zeichen, Buchstaben + Ziffern)</span></Label>
              <Input value={hostessForm.pin} onChange={e => setHostessForm(p => ({ ...p, pin: e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 32) }))} placeholder="z.B. Anna2026" data-testid="hostess-pin-input" />
            </div>
            {editingHostess && (
              <div className="flex items-center gap-2">
                <input type="checkbox" id="hostess-active" checked={hostessForm.is_active} onChange={e => setHostessForm(p => ({ ...p, is_active: e.target.checked }))} className="rounded" />
                <Label htmlFor="hostess-active" className="text-sm">Aktiv</Label>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => setShowHostessDialog(false)}>Abbrechen</Button>
              <Button onClick={saveHostess} className="bg-[#e30613] hover:bg-[#c00510]" data-testid="save-hostess-btn">Speichern</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
            <DialogDescription>
              {editingUser?.email}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="general" className="mt-4">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="general">Allgemein</TabsTrigger>
              <TabsTrigger value="permissions">Feature-Berechtigungen</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-4 mt-4">
              <div>
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="E-Mail-Adresse"
                  data-testid="edit-user-email"
                />
              </div>

              <div>
                <Label>Neues Passwort <span className="text-xs text-gray-400 font-normal">(leer lassen = unverändert)</span></Label>
                <Input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="Neues Passwort eingeben..."
                  data-testid="edit-user-password"
                />
              </div>

              <div>
                <Label>Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Name des Benutzers"
                  data-testid="edit-user-name"
                />
              </div>

              <div>
                <Label>Rolle</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(value) => setEditForm({ ...editForm, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex items-center gap-2">
                          {React.createElement(role.icon, { className: 'w-4 h-4' })}
                          {role.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Abteilung</Label>
                <Select
                  value={editForm.department_id || 'none'}
                  onValueChange={(value) => setEditForm({ ...editForm, department_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keine Abteilung" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Abteilung</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: dept.color }}
                          />
                          {dept.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                />
                <Label htmlFor="is_active">Benutzer aktiv</Label>
              </div>
            </TabsContent>

            <TabsContent value="permissions" className="space-y-4 mt-4">
              <p className="text-sm text-gray-500 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <strong>Priorität:</strong> Individuelle Berechtigungen → Abteilung → Rolle → Global
                <br />
                <span className="text-xs">Super Admin und Admin haben automatisch vollen Zugriff.</span>
              </p>

              {features.map((feature) => {
                const FeatureIcon = FEATURE_ICONS[feature.id] || Settings;
                const currentLevel = getFormPermissionLevel(feature.id);
                const currentSubPerms = getFormSubPermissions(feature.id);
                
                return (
                  <Card key={feature.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <FeatureIcon className="w-5 h-5 text-gray-500" />
                          <span className="font-semibold">{feature.name}</span>
                        </div>
                        <Select
                          value={currentLevel}
                          onValueChange={(value) => updateFeaturePermission(feature.id, value, currentSubPerms)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PERMISSION_LEVELS.map((level) => (
                              <SelectItem key={level.value} value={level.value}>
                                {level.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Sub-permissions */}
                      {currentLevel !== 'none' && feature.sub_permissions?.length > 0 && (
                        <div className="ml-7 pt-2 border-t">
                          <Label className="text-xs text-gray-500 mb-2 block">
                            Zusätzliche Fähigkeiten:
                          </Label>
                          <div className="flex flex-wrap gap-2">
                            {feature.sub_permissions.map((subPerm) => (
                              <button
                                key={subPerm}
                                onClick={() => toggleSubPermission(feature.id, subPerm)}
                                className={`text-xs px-2 py-1 rounded transition-all ${
                                  currentSubPerms.includes(subPerm)
                                    ? 'bg-[#e30613] text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
                                }`}
                              >
                                {currentSubPerms.includes(subPerm) && <Check className="w-3 h-3 inline mr-1" />}
                                {subPerm.replace(/_/g, ' ')}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>
          </Tabs>

          <div className="flex gap-3 justify-end pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={saveUser} className="bg-[#e30613] hover:bg-[#c00510]">
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Neuer Benutzer</DialogTitle>
            <DialogDescription>
              Erstellen Sie ein neues Benutzerkonto
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>E-Mail *</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="benutzer@haie.de"
                data-testid="create-user-email"
              />
            </div>

            <div>
              <Label>Passwort *</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Mind. 6 Zeichen"
                data-testid="create-user-password"
              />
            </div>

            <div>
              <Label>Name</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Vor- und Nachname"
                data-testid="create-user-name"
              />
            </div>

            <div>
              <Label>Rolle</Label>
              <Select
                value={createForm.role}
                onValueChange={(value) => setCreateForm({ ...createForm, role: value })}
              >
                <SelectTrigger data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        {React.createElement(role.icon, { className: 'w-4 h-4' })}
                        {role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Abteilung</Label>
              <Select
                value={createForm.department_id || 'none'}
                onValueChange={(value) => setCreateForm({ ...createForm, department_id: value === 'none' ? '' : value })}
              >
                <SelectTrigger data-testid="create-user-department">
                  <SelectValue placeholder="Keine Abteilung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Abteilung</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />
                        {dept.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={createUser} className="bg-[#e30613] hover:bg-[#c00510]" data-testid="create-user-submit">
                Benutzer erstellen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
